import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { siteConfig } from '../config/site';

// Valores por omissão do site. Cada página pode sobrepor-se a estes
// renderizando <PageMeta />, que corre depois deste efeito.
const MetaTags = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isDesentupimentos = siteConfig.domain === 'alphadesentupimentos.pt';
    const isEletrica = siteConfig.domain === 'alphaeletrica.pt' || siteConfig.domain === 'alphaeletric.pt';
    const isEletrodomesticos = siteConfig.domain === 'alphaservices.pt';
    const isClimatizacao = siteConfig.domain === 'alphaclimatizacao.pt';
    const isClean = siteConfig.domain === 'alphaclean.pt';
    const cityName = siteConfig.address.city;

    // Atualizar título
    let title = '';
    if (isClean) {
      title = `ALPHA CLEAN - Empresa de Limpeza em ${cityName} | Condomínios, Escritórios e Pós-Obra`;
    } else if (isClimatizacao) {
      title = `ALPHA CLIMATIZAÇÃO - Ar Condicionado em ${cityName} | Instalação e Manutenção`;
    } else if (isDesentupimentos) {
      title = `ALPHA DESENTUPIMENTOS - Desentupimentos Urgentes 24h em ${cityName}`;
    } else if (isEletrica) {
      title = `ALPHA ELÉTRICA - Eletricista 24h em ${cityName} - Reparações e Instalações`;
    } else if (isEletrodomesticos) {
      title = `ALPHA SERVICES - Instalação de Eletrodomésticos em ${cityName}`;
    } else {
      title = `ALPHA CANALIZAÇÕES - Serviços de Canalização em ${cityName}`;
    }
    document.title = title;

    // Atualizar meta description
    let description = '';
    if (isClean) {
      description = `ALPHA CLEAN - Empresa de limpeza profissional em ${cityName}. Condomínios, pós-obra, escritórios, limpeza doméstica, alojamento local, restaurantes e lojas. Equipas fixas e orçamento gratuito.`;
    } else if (isClimatizacao) {
      description = `ALPHA CLIMATIZAÇÃO - Manutenção, instalação e venda de ar condicionado em ${cityName}. Pré-instalação para obra e peças de reposição. Técnicos certificados, garantia e orçamento gratuito.`;
    } else if (isDesentupimentos) {
      description = `ALPHA DESENTUPIMENTOS - Especialistas em desentupimentos urgentes 24h em ${cityName}. Desentupimento de esgotos, sanitas, pias e colunas prediais. Hidrojato e vídeo inspeção. Técnicos locais, atendimento 24h, orçamento gratuito.`;
    } else if (isEletrica) {
      description = `ALPHA ELÉTRICA - Eletricistas certificados em ${cityName}. Reparação de avarias elétricas, quadros elétricos, iluminação LED e instalações. Serviço urgente 24h, técnicos credenciados e orçamento gratuito.`;
    } else if (isEletrodomesticos) {
      description = `ALPHA SERVICES - Especialistas em instalação profissional de eletrodomésticos em ${cityName}. Placas, fornos, máquinas de lavar, frigoríficos e termoacumuladores. Preços fixos e garantia incluída.`;
    } else {
      description = `ALPHA CANALIZAÇÕES - Especialistas em serviços de canalização em ${cityName}. Reparação urgente, instalação, desentupimentos e manutenção. Técnicos locais, atendimento 24h, orçamento gratuito.`;
    }

    // Atualizar ou criar meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Atualizar keywords
    let keywords = '';
    if (isClean) {
      keywords = `limpeza ${cityName}, empresa de limpeza ${cityName}, limpeza condomínios, limpeza pós-obra, limpeza escritórios, limpeza residencial, limpeza alojamento local, limpeza restaurantes, limpeza lojas`;
    } else if (isClimatizacao) {
      keywords = `ar condicionado ${cityName}, instalação ar condicionado ${cityName}, manutenção ar condicionado, limpeza ar condicionado, venda ar condicionado, pré-instalação ar condicionado obra, peças ar condicionado, multi-split, bomba de calor`;
    } else if (isDesentupimentos) {
      keywords = `desentupimento ${cityName}, desentupimento urgente, desentupimento 24h, desentupimento de esgotos, desentupimento de sanitas, hidrojato, vídeo inspeção`;
    } else if (isEletrica) {
      keywords = `eletricista ${cityName}, eletricista urgente, eletricista 24h, reparação elétrica, quadros elétricos, instalação elétrica, iluminação LED, certificado energético`;
    } else if (isEletrodomesticos) {
      keywords = `instalação eletrodomésticos ${cityName}, instalação placa indução, instalação forno, instalação máquina lavar, instalação frigorífico, instalação termoacumulador`;
    } else {
      keywords = `canalização ${cityName}, desentupimentos ${cityName}, reparação canalização, canalizador ${cityName}, urgente canalização`;
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);

    // Atualizar canonical URL
    // O canonical tem de ser o URL desta página. Estava fixo na raiz, o que
    // declarava todas as páginas do site como duplicados da homepage.
    const canonicalUrl = `https://www.${siteConfig.domain}${pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // Atualizar Open Graph tags
    const updateOrCreateMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    let ogTitle = '';
    if (isClean) {
      ogTitle = `ALPHA CLEAN - Empresa de Limpeza em ${cityName}`;
    } else if (isClimatizacao) {
      ogTitle = `ALPHA CLIMATIZAÇÃO - Ar Condicionado em ${cityName}`;
    } else if (isDesentupimentos) {
      ogTitle = `ALPHA DESENTUPIMENTOS - Desentupimentos Urgentes 24h em ${cityName}`;
    } else if (isEletrica) {
      ogTitle = `ALPHA ELÉTRICA - Eletricista 24h em ${cityName}`;
    } else if (isEletrodomesticos) {
      ogTitle = `ALPHA SERVICES - Instalação de Eletrodomésticos em ${cityName}`;
    } else {
      ogTitle = `ALPHA CANALIZAÇÕES - Serviços de Canalização em ${cityName}`;
    }

    let ogDescription = '';
    if (isClean) {
      ogDescription = `Empresa de limpeza profissional em ${cityName}. Condomínios, pós-obra, escritórios, residências, alojamento local, restaurantes e lojas. Orçamento gratuito.`;
    } else if (isClimatizacao) {
      ogDescription = `Manutenção, instalação e venda de ar condicionado em ${cityName}. Pré-instalação para obra e peças de reposição. Orçamento gratuito.`;
    } else if (isDesentupimentos) {
      ogDescription = `Especialistas em desentupimentos urgentes 24h em ${cityName}. Desentupimento de esgotos, sanitas, pias e colunas prediais. Hidrojato e vídeo inspeção. Orçamento gratuito, técnicos locais, atendimento 24h.`;
    } else if (isEletrica) {
      ogDescription = `Eletricistas certificados em ${cityName}. Reparação de avarias, quadros e instalações. Serviço urgente 24h. Orçamento gratuito.`;
    } else if (isEletrodomesticos) {
      ogDescription = `Especialistas em instalação de eletrodomésticos em ${cityName}. Placas, fornos, máquinas de lavar, frigoríficos e termoacumuladores. Preços fixos e garantia incluída.`;
    } else {
      ogDescription = `Especialistas em canalização em ${cityName}. Reparação urgente, desentupimentos, instalação e manutenção. Orçamento gratuito, técnicos locais, atendimento 24h.`;
    }

    updateOrCreateMeta('og:title', ogTitle);
    updateOrCreateMeta('og:description', ogDescription);
    updateOrCreateMeta('og:url', canonicalUrl);

    // Atualizar structured data (JSON-LD)
    const updateStructuredData = () => {
      let scriptTag = document.querySelector('script[type="application/ld+json"]:not([data-page-meta])');

      let serviceType = [];
      if (isClean) {
        serviceType = ["Limpeza de Condomínios", "Limpeza Pós-Obra", "Limpeza de Escritórios", "Limpeza Residencial", "Limpeza de Alojamento Local", "Limpeza de Restaurantes", "Limpeza de Lojas e Comércio"];
      } else if (isClimatizacao) {
        serviceType = ["Manutenção de Ar Condicionado", "Instalação de Ar Condicionado", "Venda de Equipamentos de Climatização", "Pré-Instalação para Obra", "Peças de Reposição"];
      } else if (isDesentupimentos) {
        serviceType = ["Desentupimento de Esgotos", "Desentupimento de Sanitas", "Desentupimento de Pias", "Hidrojato", "Vídeo Inspeção"];
      } else if (isEletrica) {
        serviceType = ["Instalação Elétrica", "Reparação de Avarias", "Quadros Elétricos", "Iluminação LED", "Certificação Elétrica"];
      } else {
        serviceType = ["Reparação de Canalização", "Desentupimentos", "Instalação de Canalização", "Manutenção"];
      }

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": siteConfig.name,
        "url": canonicalUrl,
        "description": isClean
          ? `Empresa de limpeza profissional em ${cityName}`
          : isClimatizacao
          ? `Especialistas em ar condicionado e climatização em ${cityName}`
          : isDesentupimentos
          ? `Especialistas em desentupimentos urgentes 24h em ${cityName}`
          : isEletrica
            ? `Especialistas em serviços elétricos, reparações e instalações em ${cityName}`
            : "Especialistas em serviços de canalização, reparação e instalação",
        "telephone": siteConfig.phone,
        "email": siteConfig.email,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": siteConfig.address.street,
          "addressLocality": siteConfig.address.city,
          "postalCode": siteConfig.address.postalCode,
          "addressCountry": siteConfig.address.country
        },
        "areaServed": [cityName, "Portugal"],
        "serviceType": serviceType,
        "openingHours": "Mo-Su 00:00-23:59",
        "priceRange": "$$"
      };

      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(structuredData);
    };

    updateStructuredData();
  }, [pathname]);

  return null;
};

export default MetaTags;

